import { useMemo, useState } from 'react'
import { Building2, Landmark } from 'lucide-react'
import { useController, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal, Button, Input, Select, DateInput, SegmentedControl } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import {
  useCreateExpense,
  useUpdateExpense,
  useExpenseCategories,
  useCreateExpenseCategory,
} from '@/features/expenses/hooks/useExpenses'
import {
  expenseFormSchema,
  getTodayDateString,
  type ExpenseFormData,
} from '@/features/expenses/schemas/expense.schema'
import {
  PREDEFINED_EXPENSE_CATEGORIES,
  PAYMENT_METHODS,
  type ExpenseAttachment,
  type ExpenseEntry,
  type PaymentMethod,
} from '@/features/expenses/types/expense.types'
import {
  expenseCategoryLabel,
  paymentMethodLabel,
} from '@/features/expenses/components/expenseUtils'
import { useOrgCurrency } from '@/hooks/useOrgCurrency'
import { ExpenseAttachments } from './ExpenseAttachments'

const PAYMENT_METHOD_OPTIONS: SelectOption[] = [
  { value: '', label: 'Select method…' },
  ...PAYMENT_METHODS.map((m) => ({ value: m, label: paymentMethodLabel(m) })),
]

const ADD_CUSTOM_VALUE = '__add_custom__'
type ExpenseType = 'organization' | 'company'

const EXPENSE_TYPE_OPTIONS = [
  { value: 'organization' as const, label: 'Organization', icon: Landmark },
  { value: 'company' as const, label: 'Company', icon: Building2 },
]

interface ExpenseFormModalProps {
  /** Companies a company expense can belong to. */
  companies: { _id: string; name: string }[]
  /** Starting expense type and company when adding. Ignored when editing. */
  defaults: { type: ExpenseType; companyId: string | null }
  open: boolean
  onClose: () => void
  /** When provided, the modal edits this expense instead of creating a new one. */
  expense?: ExpenseEntry | null
}

export function ExpenseFormModal({
  companies,
  defaults,
  open,
  onClose,
  expense,
}: ExpenseFormModalProps): React.ReactElement | null {
  const isEdit = !!expense
  const createMutation = useCreateExpense()
  const updateMutation = useUpdateExpense()
  const createCategoryMutation = useCreateExpenseCategory()
  const { data: customCategories = [] } = useExpenseCategories({ enabled: open })

  // Organization expense or company expense. Editing keeps it as it is: the
  // API moves nothing between the two, so the fields show but cannot change.
  const [expenseType, setExpenseType] = useState<ExpenseType>(
    isEdit ? (expense?.companyId ? 'company' : 'organization') : defaults.type,
  )
  const [companyChoice, setCompanyChoice] = useState<string>(
    (isEdit ? expense?.companyId : defaults.companyId) ?? '',
  )
  const [companyError, setCompanyError] = useState<string | null>(null)
  const companyId = expenseType === 'company' ? companyChoice || null : null
  const companyOptions: SelectOption[] = useMemo(() => {
    const options = companies.map((c) => ({ value: c._id, label: c.name }))
    // An expense on a company this user cannot pick still names it when edited.
    if (companyChoice && !companies.some((c) => c._id === companyChoice)) {
      options.push({ value: companyChoice, label: 'Another company' })
    }
    return [{ value: '', label: 'Select company…' }, ...options]
  }, [companies, companyChoice])

  const [customCategoryInput, setCustomCategoryInput] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)
  const { currency: orgCurrency, currencyOptions } = useOrgCurrency()

  const buildDefaults = (): ExpenseFormData =>
    expense
      ? {
          title: expense.title,
          category: expense.category,
          amount: expense.amount,
          currency: expense.currency,
          date: expense.date?.slice(0, 10) || getTodayDateString(),
          paymentMethod: expense.paymentMethod,
          description: expense.description ?? '',
          attachments: expense.attachments ?? [],
        }
      : {
          title: '',
          category: '',
          amount: undefined as unknown as number,
          currency: orgCurrency,
          date: getTodayDateString(),
          paymentMethod: undefined as unknown as ExpenseFormData['paymentMethod'],
          description: '',
          attachments: [],
        }

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ExpenseFormData>({
    resolver: zodResolver(expenseFormSchema),
    // The parent re-keys this modal per expense (or per new-expense defaults), so defaults init per target.
    defaultValues: buildDefaults(),
  })

  const { field: categoryField } = useController({ name: 'category', control })
  const { field: currencyField } = useController({ name: 'currency', control })
  const { field: paymentMethodField } = useController({ name: 'paymentMethod', control })
  const { field: attachmentsField } = useController({ name: 'attachments', control })
  const attachments = (attachmentsField.value ?? []) as ExpenseAttachment[]

  const categoryOptions: SelectOption[] = useMemo(() => {
    const custom = customCategories.map((c) => ({ value: c.name, label: c.name }))
    const selectedCategory = categoryField.value ?? ''
    const selectedCustom =
      selectedCategory &&
      selectedCategory !== ADD_CUSTOM_VALUE &&
      !PREDEFINED_EXPENSE_CATEGORIES.includes(
        selectedCategory as (typeof PREDEFINED_EXPENSE_CATEGORIES)[number],
      ) &&
      !custom.some((c) => c.value === selectedCategory)
        ? [{ value: selectedCategory, label: selectedCategory }]
        : []
    return [
      { value: '', label: 'Select category…' },
      ...PREDEFINED_EXPENSE_CATEGORIES.map((c) => ({
        value: c,
        label: expenseCategoryLabel(c),
      })),
      ...custom,
      ...selectedCustom,
      { value: ADD_CUSTOM_VALUE, label: '+ Add custom category…' },
    ]
  }, [categoryField.value, customCategories])

  const handleCategoryChange = (value: string): void => {
    if (value === ADD_CUSTOM_VALUE) {
      setShowCustomInput(true)
      categoryField.onChange('')
      return
    }
    setShowCustomInput(false)
    categoryField.onChange(value)
  }

  const applyCustomCategory = (): void => {
    const trimmed = customCategoryInput.trim()
    if (!trimmed) return
    const alreadyExists =
      PREDEFINED_EXPENSE_CATEGORIES.includes(
        trimmed as (typeof PREDEFINED_EXPENSE_CATEGORIES)[number],
      ) || customCategories.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())

    const selectCategory = (): void => {
      setValue('category', trimmed, { shouldValidate: true, shouldDirty: true })
      setCustomCategoryInput('')
      setShowCustomInput(false)
    }

    if (alreadyExists) {
      selectCategory()
      return
    }

    createCategoryMutation.mutate(
      { name: trimmed },
      {
        onSuccess: (created) => {
          setValue('category', created.name, { shouldValidate: true, shouldDirty: true })
          setCustomCategoryInput('')
          setShowCustomInput(false)
        },
      },
    )
  }

  const isPending =
    createMutation.isPending || updateMutation.isPending || createCategoryMutation.isPending

  const onSubmit = (data: ExpenseFormData): void => {
    if (expenseType === 'company' && !companyChoice) {
      setCompanyError('Choose the company this expense belongs to.')
      return
    }
    const body = {
      title: data.title,
      category: data.category,
      amount: data.amount,
      currency: data.currency,
      date: data.date,
      description: data.description || undefined,
      paymentMethod: data.paymentMethod as PaymentMethod,
      attachments: (data.attachments ?? []).filter((a) => a.id && a.url),
    }
    const onDone = (): void => {
      if (!isEdit) {
        reset({
          title: '',
          category: '',
          amount: undefined as unknown as number,
          currency: 'PKR',
          date: getTodayDateString(),
          paymentMethod: undefined as unknown as ExpenseFormData['paymentMethod'],
          description: '',
          attachments: [],
        })
        setCustomCategoryInput('')
        setShowCustomInput(false)
      }
      onClose()
    }
    if (isEdit && expense) {
      updateMutation.mutate({ companyId, id: expense._id, body }, { onSuccess: onDone })
    } else {
      createMutation.mutate({ companyId, body }, { onSuccess: onDone })
    }
  }

  const handleClose = (): void => {
    onClose()
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEdit ? 'Edit expense' : 'Add expense'}
      className="max-w-2xl"
      footer={
        <div className="flex gap-2">
          <Button type="submit" form="expense-form" variant="primary" loading={isPending}>
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add expense'}
          </Button>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
        </div>
      }
    >
      <form id="expense-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <p className="mb-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
            Expense type <span className="text-red-500">*</span>
          </p>
          <SegmentedControl
            ariaLabel="Expense type"
            options={EXPENSE_TYPE_OPTIONS}
            value={expenseType}
            onChange={(next) => {
              setExpenseType(next)
              setCompanyError(null)
            }}
            disabled={isEdit}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {expenseType === 'organization'
              ? 'The organization itself carries it, not one company — e.g. head-office rent.'
              : 'It belongs to one company and counts in that company’s expenses.'}
          </p>
        </div>

        {expenseType === 'company' && (
          <div>
            <label
              htmlFor="expense-company"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Company <span className="text-red-500">*</span>
            </label>
            <Select
              id="expense-company"
              options={companyOptions}
              value={companyChoice}
              onChange={(value) => {
                setCompanyChoice(value)
                setCompanyError(null)
              }}
              disabled={isEdit}
              searchable={companyOptions.length > 8}
            />
            {companyError && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{companyError}</p>
            )}
          </div>
        )}

        <Input
          {...register('title')}
          id="expense-title"
          label="Title"
          required
          placeholder="e.g. Office chairs"
          error={errors.title?.message}
        />

        <div>
          <label
            htmlFor="expense-category"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Category <span className="text-red-500">*</span>
          </label>
          <Select
            id="expense-category"
            options={categoryOptions}
            value={showCustomInput ? ADD_CUSTOM_VALUE : (categoryField.value ?? '')}
            onChange={handleCategoryChange}
            placeholder="Select category…"
            searchable
          />
          {showCustomInput && (
            <div className="mt-2 flex gap-2">
              <Input
                value={customCategoryInput}
                onChange={(e) => setCustomCategoryInput(e.target.value)}
                placeholder="New category name"
                aria-label="New category name"
                disabled={createCategoryMutation.isPending}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={applyCustomCategory}
                loading={createCategoryMutation.isPending}
              >
                {createCategoryMutation.isPending ? 'Adding…' : 'Use'}
              </Button>
            </div>
          )}
          {errors.category && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.category.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            {...register('amount', { valueAsNumber: true })}
            id="expense-amount"
            label="Amount"
            type="number"
            step="0.01"
            min="0.01"
            required
            placeholder="0.00"
            error={errors.amount?.message}
          />
          <div>
            <label
              htmlFor="expense-currency"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Currency
            </label>
            <Select
              id="expense-currency"
              options={currencyOptions}
              value={currencyField.value ?? orgCurrency}
              onChange={currencyField.onChange}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <DateInput
            {...register('date')}
            id="expense-date"
            label="Date of expense"
            required
            error={errors.date?.message}
          />
          <div>
            <label
              htmlFor="expense-payment-method"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Payment method <span className="text-red-500">*</span>
            </label>
            <Select
              id="expense-payment-method"
              options={PAYMENT_METHOD_OPTIONS}
              value={paymentMethodField.value ?? ''}
              onChange={paymentMethodField.onChange}
            />
            {errors.paymentMethod && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {errors.paymentMethod.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="expense-description"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Description / Notes
          </label>
          <textarea
            {...register('description')}
            id="expense-description"
            rows={3}
            placeholder="Optional notes about this expense…"
            className="box-border w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
          {errors.description && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {errors.description.message}
            </p>
          )}
        </div>

        <ExpenseAttachments
          value={attachments}
          onChange={(next) => attachmentsField.onChange(next)}
          disabled={isPending}
        />
      </form>
    </Modal>
  )
}

ExpenseFormModal.displayName = 'ExpenseFormModal'
